// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { LabTrackerRow } from '../../api/client'

// CareRx wave-1 #2: every Lab Tracker tile is a filter. A tile's number is `rows.filter(match)`
// and a click shows exactly `rows.filter(match)`, so the count and the list cannot disagree.
// Owner review 2026-08-13 still holds: "not contacted" means NO attempt yet (unreachable is its
// own retry queue).

export type LabTileKey = 'all' | 'need_provider' | 'pt_not_contacted' | 'pt_unreachable' | 'prov_not_contacted' | 'redraws' | 'ready'

export interface LabTileDef { key: LabTileKey; label: string; tone: 'ok' | 'warn' | 'bad' | undefined; match: (r: LabTrackerRow) => boolean }

export const LAB_TILES: LabTileDef[] = [
  { key: 'all', label: 'In tracker', tone: undefined, match: () => true },
  { key: 'need_provider', label: 'Need lab provider', tone: 'warn', match: r => !r.lab_provider_id },
  { key: 'pt_not_contacted', label: 'Patient not contacted', tone: 'warn', match: r => (r.patient_outreach_status ?? 'not_contacted') === 'not_contacted' },
  { key: 'pt_unreachable', label: 'Unreachable — retry', tone: 'warn', match: r => r.patient_outreach_status === 'unreachable' },
  { key: 'prov_not_contacted', label: 'Provider not contacted', tone: 'warn', match: r => (r.provider_outreach_status ?? 'not_contacted') === 'not_contacted' },
  // Counts THERAPIES with an open re-draw (what a click lists), not orders — the order total
  // rides along as the tile's hint when it differs.
  { key: 'redraws', label: 'Re-draws open', tone: 'bad', match: r => (r.redraw_count || 0) > 0 },
  { key: 'ready', label: 'Ready for review', tone: 'ok', match: r => r.open_count === 0 },
]

/** The rows a tile selection shows. `null` (no tile) is every row. */
export function labTileRows(rows: LabTrackerRow[], tile: LabTileKey | null): LabTrackerRow[] {
  const def = tile ? LAB_TILES.find(t => t.key === tile) : undefined
  return def ? rows.filter(def.match) : rows
}

/** Clicking the active tile clears it; clicking another selects it. "In tracker" is the cleared state. */
export const nextLabTile = (current: LabTileKey | null, clicked: LabTileKey): LabTileKey | null =>
  clicked === 'all' || current === clicked ? null : clicked
